import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabaseClient';

interface Props { isOpen: boolean; onClose: () => void; }

export default function PreferencesModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [saved, setSaved] = useState<null | 'ok' | 'err'>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const { data } = await supabase.auth.getUser();
        const meta = (data?.user as any)?.user_metadata || {};
        setMarketingOptIn(Boolean(meta?.marketing_opt_in));
      } catch {
      } finally {
        setLoading(false);
      }
    }
    if (isOpen) { setSaved(null); load(); }
  }, [isOpen]);

  if (!isOpen) return null;

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true); setSaved(null);
    try {
      const data: Record<string, any> = { marketing_opt_in: marketingOptIn };
      if (marketingOptIn) data.marketing_opt_in_at = Date.now();
      await supabase.auth.updateUser({ data });
      setSaved('ok');
      onClose();
    } catch {
      setSaved('err');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex min-h-screen items-center justify-center bg-black/70 backdrop-blur-sm p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="preferences-title">
      <div className="w-full max-w-md bg-gray-900 rounded-lg sm:rounded-xl shadow-2xl border border-pink-600/20">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-800">
          <h3 id="preferences-title" className="text-lg font-semibold text-white">{t('prefs.title', 'Privacy preferences')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white px-2 py-1 rounded">✕</button>
        </div>
        <form onSubmit={handleSave} className="p-5 sm:p-6 space-y-4 text-gray-200">
          <fieldset disabled={loading} className="space-y-3">
            <div className="flex items-start gap-3">
              <input id="pref-marketing" type="checkbox" className="mt-0.5 h-4 w-4" checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)} />
              <label htmlFor="pref-marketing" className="text-sm">
                <div className="font-medium">{t('prefs.marketingOptIn', 'Receive promotions by email/SMS')}</div>
                <div className="text-xs text-gray-400">{t('prefs.marketingOptInDesc', 'We will only send promotions if you opt in. You can opt out anytime from this screen or via unsubscribe links.')}</div>
              </label>
            </div>
          </fieldset>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-md bg-pink-600 hover:bg-pink-700 disabled:opacity-50 text-white font-semibold">{t('prefs.save', 'Save')}</button>
          </div>
          {saved === 'ok' && (<div className="text-xs text-emerald-300">{t('prefs.saved', 'Saved')}</div>)}
          {saved === 'err' && (<div className="text-xs text-red-300">{t('prefs.saveError', 'Could not save preferences. Please try again.')}</div>)}
        </form>
      </div>
    </div>
  );
}


